import { useEffect, useState } from 'react'
import { CheckCircle2, Globe2, Sparkles } from 'lucide-react'
import { api, Geopricing } from '../lib/api'
import clsx from 'clsx'
import { useAuthStore } from '../store/authStore'
import { useNavigate } from 'react-router-dom'
import { normalizePlanFromApi, planNameToSlug, planRank, type PlanSlug } from '../lib/planTier'

export function PricingScreen() {
  const [pricing, setPricing] = useState<Geopricing | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAnnual, setIsAnnual] = useState(true)
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [upgrading, setUpgrading] = useState<string | null>(null)
  const [currentPlan, setCurrentPlan] = useState<PlanSlug | null>(null)

  useEffect(() => {
    api.getGeoPricing()
      .then(setPricing)
      .catch((err) => console.error('Failed to load pricing:', err))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!user) {
      setCurrentPlan(null)
      return
    }
    api
      .getOrgSettings()
      .then((s) => setCurrentPlan(normalizePlanFromApi(s.plan)))
      .catch(() => setCurrentPlan(null))
  }, [user])

  async function handleUpgrade(planName: string) {
    if (!user) {
      navigate('/login')
      return
    }
    const plan = planNameToSlug(planName)
    setUpgrading(planName)
    try {
      await api.upgradePlan(plan)
      window.location.href = '/settings?upgraded=true'
    } catch (err) {
      console.error('Upgrade failed:', err)
      alert('Upgrade failed. Please try again or contact support.')
    } finally {
      setUpgrading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex flex-col p-8 md:p-12 items-center justify-center min-h-[400px]">
        <div className="h-8 w-32 rounded animate-pulse bg-warm-inset mb-4" />
        <div className="h-4 w-64 rounded animate-pulse bg-warm-inset" />
      </div>
    )
  }

  if (!pricing) {
    return (
      <div className="flex-1 flex flex-col p-8 md:p-12 items-center justify-center min-h-[400px]">
        <p className="text-ink-muted">Unable to load pricing information at this time.</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto bg-warm-base p-8 md:p-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-warm-border bg-warm-card text-brand-teal text-xs font-semibold uppercase tracking-widest shadow-sm mb-6">
            <Globe2 size={13} /> Pricing fixed for {pricing.countryName}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-ink mb-4" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>
            Simple pricing for high-performing teams
          </h1>
          <p className="text-ink-muted max-w-2xl mx-auto text-[15px] leading-relaxed">
            {pricing.pppNote} {pricing.billingNote}
          </p>
          {pricing.promoBadge && (
            <p className="mt-4 text-sm font-medium text-brand-teal">{pricing.promoBadge}</p>
          )}

          <div className="mt-8 flex items-center justify-center gap-3">
            <span className={clsx('text-sm font-medium transition-colors', !isAnnual ? 'text-ink' : 'text-ink-muted')}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className="relative w-12 h-6 rounded-full bg-warm-border transition-colors hover:bg-ink-soft cursor-pointer overflow-hidden border border-warm-card shadow-inner"
              style={{ background: isAnnual ? '#2A7A8A' : undefined }}
            >
              <div
                className={clsx(
                  'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ease-spring',
                  isAnnual ? 'translate-x-6' : 'translate-x-0'
                )}
              />
            </button>
            <span className={clsx('text-sm font-medium transition-colors flex items-center gap-1.5', isAnnual ? 'text-ink' : 'text-ink-muted')}>
              Annually <span className="text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded shadow-sm">Save 20%</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {pricing.plans.map((plan) => {
            const price = isAnnual ? plan.annualPrice : plan.monthlyPrice
            const targetSlug = planNameToSlug(plan.name)
            const samePlan = currentPlan !== null && targetSlug === currentPlan

            return (
              <div
                key={plan.name}
                className="relative flex flex-col rounded-2xl border p-6 transition-all hover:-translate-y-1"
                style={plan.highlight ? {
                  background: 'linear-gradient(160deg, #1e5060 0%, #2A7A8A 100%)',
                  borderColor: '#3a9bae',
                  boxShadow: '0 8px 40px -8px rgba(42,122,138,0.5)',
                } : {
                  background: '#FDFCF8',
                  borderColor: '#e0d9cc',
                  boxShadow: '0 2px 16px rgba(42,122,138,0.07)',
                }}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-md flex items-center gap-1"
                    style={{ background: '#1a4a58' }}
                  >
                    <Sparkles size={12} /> Most Popular
                  </div>
                )}

                <h3 className="text-lg font-bold mb-1" style={{ color: plan.highlight ? '#fff' : '#1a3a40' }}>
                  {plan.name}
                </h3>
                <p className="text-sm mb-6 font-medium" style={{ color: plan.highlight ? 'rgba(255,255,255,0.7)' : '#6b8a8f' }}>
                  {plan.userLimit ? `Up to ${plan.userLimit.toLocaleString()} users` : 'Unlimited users'}
                </p>

                <div className="mb-6 pb-6 border-b" style={{ borderColor: plan.highlight ? 'rgba(255,255,255,0.15)' : '#e0d9cc' }}>
                  <div className="flex items-end gap-1 mb-1" style={{ color: plan.highlight ? '#fff' : '#1a3a40' }}>
                    <span className="text-2xl font-semibold leading-none">{plan.symbol}</span>
                    <span className="text-4xl font-black leading-none tracking-tight">
                      {price === 0 ? '0' : Math.round(price)}
                    </span>
                  </div>
                  <div className="text-[13px]" style={{ color: plan.highlight ? 'rgba(255,255,255,0.65)' : '#3d6068' }}>
                    per user {isAnnual ? '/ month, billed annually' : '/ month'}
                  </div>
                </div>

                <ul className="flex-1 space-y-3 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5">
                      <CheckCircle2
                        size={16}
                        className="shrink-0 mt-0.5"
                        style={{ color: plan.highlight ? '#a7d8e0' : '#2A7A8A' }}
                      />
                      <span className="text-sm leading-snug" style={{ color: plan.highlight ? 'rgba(255,255,255,0.88)' : '#3d6068' }}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleUpgrade(plan.name)}
                  disabled={!!upgrading || samePlan}
                  className="w-full py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50"
                  style={plan.highlight ? {
                    background: 'rgba(255,255,255,0.15)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.3)',
                  } : {
                    background: '#2A7A8A',
                    color: '#fff',
                    border: 'none',
                  }}
                >
                  {samePlan
                    ? 'Current plan'
                    : upgrading === plan.name
                      ? 'Updating…'
                      : user
                        ? (currentPlan !== null && planRank(targetSlug) < planRank(currentPlan)
                            ? `Downgrade to ${plan.name}`
                            : `Choose ${plan.name}`)
                        : 'Get started'}
                </button>
              </div>
            )
          })}
        </div>

        <div
          className="mt-12 max-w-2xl mx-auto rounded-2xl border p-6 text-center"
          style={{ background: '#FDFCF8', borderColor: '#e0d9cc' }}
        >
          <h2 className="text-sm font-semibold text-ink mb-2">Need more than the Free tier (50 people)?</h2>
          <p className="text-sm text-ink-muted leading-relaxed mb-4">
            Paid tiers in the app update your plan flag for pilots. For SSO, unlimited seats, or procurement, email{' '}
            <a href="mailto:support@getduva.com?subject=Duva%20upgrade%20/%20enterprise" className="text-brand-teal font-medium underline">
              support@getduva.com
            </a>{' '}
            — we aim to reply within <strong className="text-ink">one business day</strong>. Or join the{' '}
            <a
              href="https://www.getduva.com/#waitlist-cta"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-teal font-medium underline"
            >
              enterprise waitlist
            </a>{' '}
            on the marketing site.
          </p>
          <p className="text-xs text-ink-muted">
            If an upgrade action fails, use the same contact — billing integrations may lag behind your growth.
          </p>
        </div>
      </div>
    </div>
  )
}
